import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MapPin, LocateFixed, Loader2 } from 'lucide-react';
import { useAddressSearch } from '../../hooks/useAddressSearch';
import api from '../../api';

// Ola/Uber-style address field: typeahead suggestions, optional "use current location".
export const AddressAutocomplete = ({
  id, label, placeholder, value, place, onChange, onSelect, bias, userCoords, iconColor = 'text-muted-foreground/70', testId,
}) => {
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const enabled = !place || value !== place.full;
  const { suggestions, loading } = useAddressSearch(value, bias, enabled);
  const showList = open && enabled && (suggestions.length > 0 || loading || (userCoords && value.trim().length < 3));

  const pick = (p) => {
    onSelect(p);
    setOpen(false);
  };

  const useCurrentLocation = async () => {
    if (!userCoords) return;
    setLocating(true);
    try {
      const { data } = await api.get('/api/geo/reverse', { params: { lat: userCoords.lat, lng: userCoords.lng } });
      pick(data);
    } catch {
      pick({ label: 'Current location', secondary: '', full: 'Current location', lat: userCoords.lat, lng: userCoords.lng });
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="relative">
      <Label htmlFor={id} className="text-base mb-2 block">{label}</Label>
      <div className="relative">
        <MapPin className={`absolute left-3 top-3.5 w-5 h-5 ${iconColor}`} />
        <Input
          id={id}
          autoComplete="off"
          placeholder={placeholder}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="pl-10 pr-10 h-12"
          data-testid={testId}
        />
        {loading && <Loader2 className="absolute right-3 top-3.5 w-5 h-5 animate-spin text-muted-foreground/70" />}
      </div>
      {place && value === place.full && (
        <p className="text-xs text-green-700 mt-1 flex items-center gap-1" data-testid={`${testId}-selected`}>
          <MapPin className="w-3 h-3" /> Location pinned on map
        </p>
      )}
      {showList && (
        <ul className="absolute z-[60] mt-1 w-full bg-white border border-border rounded-lg shadow-xl overflow-hidden"
          data-testid={`${testId}-suggestions`}>
          {userCoords && value.trim().length < 3 && (
            <li>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={useCurrentLocation}
                className="w-full text-left px-3 py-2.5 hover:bg-accent/10 flex items-center gap-2 text-primary text-sm font-medium"
                data-testid={`${testId}-use-current`}>
                {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
                Use my current location
              </button>
            </li>
          )}
          {suggestions.map((s, i) => (
            <li key={`${s.lat}-${s.lng}-${i}`}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(s)}
                className="w-full text-left px-3 py-2.5 hover:bg-background flex items-start gap-2"
                data-testid={`${testId}-suggestion-${i}`}>
                <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground/70 flex-shrink-0" />
                <span>
                  <span className="block text-sm font-medium text-foreground">{s.label}</span>
                  {s.secondary && <span className="block text-xs text-muted-foreground">{s.secondary}</span>}
                </span>
              </button>
            </li>
          ))}
          {!loading && suggestions.length === 0 && value.trim().length >= 3 && (
            <li className="px-3 py-2.5 text-sm text-muted-foreground">No matching places found</li>
          )}
        </ul>
      )}
    </div>
  );
};
